import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    CircularProgress,
    IconButton,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import moment from 'moment';
import AdminService from '../../services/AdminService';

const num = (value) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
};

const dash = (value, show) => (show ? value : '—');

const buildSummaryTree = (budget) => {
    if (!budget) return null;

    const cities = budget.cities || [];
    const spaceUsage = budget.space_usage || [];
    const spacesByCityId = new Map();
    const unassignedSpaces = [];

    spaceUsage.forEach((space) => {
        if (space.city_budget_id != null) {
            const list = spacesByCityId.get(space.city_budget_id) || [];
            list.push(space);
            spacesByCityId.set(space.city_budget_id, list);
        } else {
            unassignedSpaces.push(space);
        }
    });

    const countriesMap = new Map();
    cities.forEach((city) => {
        const countryKey = `${city.country_id || 'unknown'}::${city.country_name || 'Sin país'}`;
        if (!countriesMap.has(countryKey)) {
            countriesMap.set(countryKey, {
                id: countryKey,
                label: city.country_name || `País #${city.country_id}`,
                hours: 0,
                days: 0,
                used_hours: 0,
                used_days: 0,
                cities: []
            });
        }
        const country = countriesMap.get(countryKey);
        const citySpaces = (spacesByCityId.get(city.id) || []).slice().sort((a, b) =>
            String(a.space_name || '').localeCompare(String(b.space_name || ''), undefined, { sensitivity: 'base' })
        );
        country.hours += num(city.hours);
        country.days += num(city.days);
        country.used_hours += num(city.used_hours);
        country.used_days += num(city.used_days);
        country.cities.push({
            id: city.id,
            label: city.city_name,
            hours: num(city.hours),
            days: num(city.days),
            used_hours: num(city.used_hours),
            used_days: num(city.used_days),
            spaces: citySpaces.map((space) => ({
                id: space.space_id,
                label: space.host_name
                    ? `${space.space_name || `Espacio #${space.space_id}`} · ${space.host_name}`
                    : (space.space_name || `Espacio #${space.space_id}`),
                hours: null,
                days: null,
                used_hours: num(space.used_hours),
                used_days: num(space.used_days),
                citiesCount: null
            }))
        });
    });

    const countries = Array.from(countriesMap.values())
        .map((country) => ({
            ...country,
            cities: country.cities.sort((a, b) =>
                String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' })
            ),
            citiesCount: country.cities.length
        }))
        .sort((a, b) => String(a.label || '').localeCompare(String(b.label || ''), undefined, { sensitivity: 'base' }));

    if (unassignedSpaces.length) {
        countries.push({
            id: 'unassigned',
            label: 'Sin ciudad asignada',
            hours: 0,
            days: 0,
            used_hours: unassignedSpaces.reduce((acc, s) => acc + num(s.used_hours), 0),
            used_days: unassignedSpaces.reduce((acc, s) => acc + num(s.used_days), 0),
            citiesCount: 0,
            cities: [{
                id: 'unassigned-city',
                label: '—',
                hours: 0,
                days: 0,
                used_hours: 0,
                used_days: 0,
                spaces: unassignedSpaces.map((space) => ({
                    id: space.space_id,
                    label: space.host_name
                        ? `${space.space_name || `Espacio #${space.space_id}`} · ${space.host_name}`
                        : (space.space_name || `Espacio #${space.space_id}`),
                    hours: null,
                    days: null,
                    used_hours: num(space.used_hours),
                    used_days: num(space.used_days),
                    citiesCount: null
                }))
            }]
        });
    }

    return {
        id: `month-${budget.id}`,
        label: moment(budget.budget_month).utc().format('MMMM YYYY'),
        hours: num(budget.total_hours),
        days: num(budget.total_days),
        used_hours: num(budget.used_hours),
        used_days: num(budget.used_days),
        citiesCount: cities.length,
        countries
    };
};

const flattenVisibleRows = (tree, expanded) => {
    if (!tree) return [];

    const rows = [{
        key: tree.id,
        label: tree.label,
        hours: tree.hours,
        used_hours: tree.used_hours,
        days: tree.days,
        used_days: tree.used_days,
        citiesCount: tree.citiesCount,
        depth: 0,
        bold: true,
        expandable: tree.countries.length > 0,
        expanded: expanded.month,
        onToggleKey: 'month'
    }];

    if (!expanded.month) return rows;

    tree.countries.forEach((country) => {
        const countryOpen = Boolean(expanded.countries[country.id]);
        rows.push({
            key: country.id,
            label: country.label,
            hours: country.hours,
            used_hours: country.used_hours,
            days: country.days,
            used_days: country.used_days,
            citiesCount: country.citiesCount,
            depth: 1,
            bold: true,
            expandable: country.cities.length > 0,
            expanded: countryOpen,
            onToggleKey: `country:${country.id}`
        });

        if (!countryOpen) return;

        country.cities.forEach((city) => {
            const cityOpen = Boolean(expanded.cities[city.id]);
            rows.push({
                key: `city-${city.id}`,
                label: city.label,
                hours: city.hours,
                used_hours: city.used_hours,
                days: city.days,
                used_days: city.used_days,
                citiesCount: null,
                depth: 2,
                bold: false,
                expandable: city.spaces.length > 0,
                expanded: cityOpen,
                onToggleKey: `city:${city.id}`
            });

            if (!cityOpen) return;

            city.spaces.forEach((space) => {
                rows.push({
                    key: `space-${city.id}-${space.id}`,
                    label: space.label,
                    hours: null,
                    used_hours: space.used_hours,
                    days: null,
                    used_days: space.used_days,
                    citiesCount: null,
                    depth: 3,
                    bold: false,
                    expandable: false,
                    expanded: false,
                    onToggleKey: null
                });
            });
        });
    });

    return rows;
};

export const SemiFlexibleMonthSummary = ({ companyId, budgets = [] }) => {
    const sortedBudgets = useMemo(
        () => [...budgets].sort((a, b) => new Date(b.budget_month) - new Date(a.budget_month)),
        [budgets]
    );
    const [selectedBudgetId, setSelectedBudgetId] = useState(null);
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expanded, setExpanded] = useState({ month: true, countries: {}, cities: {} });

    useEffect(() => {
        if (!sortedBudgets.length) {
            setSelectedBudgetId(null);
            setDetail(null);
            return;
        }
        const stillExists = sortedBudgets.some((b) => b.id === selectedBudgetId);
        if (!stillExists) {
            setSelectedBudgetId(sortedBudgets[0].id);
        }
    }, [sortedBudgets, selectedBudgetId]);

    useEffect(() => {
        if (!companyId || !selectedBudgetId) return undefined;

        let cancelled = false;
        const load = async () => {
            try {
                setLoading(true);
                const res = await AdminService.getCompanySemiFlexibleBudget(companyId, selectedBudgetId);
                if (!cancelled) {
                    setDetail(res?.data || null);
                    setExpanded({ month: true, countries: {}, cities: {} });
                }
            } catch (error) {
                console.log(error);
                if (!cancelled) setDetail(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [companyId, selectedBudgetId]);

    const tree = useMemo(() => buildSummaryTree(detail), [detail]);
    const rows = useMemo(() => flattenVisibleRows(tree, expanded), [tree, expanded]);

    const handleToggle = (toggleKey) => {
        if (toggleKey === 'month') {
            setExpanded((prev) => ({ ...prev, month: !prev.month }));
            return;
        }
        if (toggleKey?.startsWith('country:')) {
            const id = toggleKey.slice('country:'.length);
            setExpanded((prev) => ({
                ...prev,
                countries: { ...prev.countries, [id]: !prev.countries[id] }
            }));
            return;
        }
        if (toggleKey?.startsWith('city:')) {
            const id = toggleKey.slice('city:'.length);
            setExpanded((prev) => ({
                ...prev,
                cities: { ...prev.cities, [id]: !prev.cities[id] }
            }));
        }
    };

    if (!sortedBudgets.length) {
        return (
            <Typography variant={'body2'} color={'textSecondary'}>
                No hay presupuestos Semi-Flex para mostrar el resumen.
            </Typography>
        );
    }

    return (
        <Box>
            <Box style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12 }}>
                <Typography variant={'subtitle1'} style={{ fontWeight: 600 }}>
                    Resumen del mes
                </Typography>
                <TextField
                    select
                    size={'small'}
                    variant={'outlined'}
                    label={'Mes'}
                    value={selectedBudgetId || ''}
                    onChange={(e) => setSelectedBudgetId(Number(e.target.value))}
                    style={{ minWidth: 200 }}
                >
                    {sortedBudgets.map((budget) => (
                        <MenuItem key={budget.id} value={budget.id}>
                            {moment(budget.budget_month).utc().format('MMMM YYYY')}
                        </MenuItem>
                    ))}
                </TextField>
            </Box>

            {loading ? (
                <CircularProgress style={{ margin: '40px auto', display: 'block' }} />
            ) : !tree ? (
                <Typography variant={'body2'} color={'textSecondary'}>
                    No se pudo cargar el resumen del mes seleccionado.
                </Typography>
            ) : (
                <Table size={'small'}>
                    <TableHead>
                        <TableRow>
                            <TableCell>Mes</TableCell>
                            <TableCell align={'right'}>Horas Contratadas</TableCell>
                            <TableCell align={'right'}>Hs Usadas</TableCell>
                            <TableCell align={'right'}>Días Contratados</TableCell>
                            <TableCell align={'right'}>Días Usados</TableCell>
                            <TableCell align={'right'}>Ciudades</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.key}>
                                <TableCell style={{ paddingLeft: 8 + row.depth * 20, whiteSpace: 'nowrap' }}>
                                    <Box style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                        {row.expandable ? (
                                            <IconButton
                                                size={'small'}
                                                onClick={() => handleToggle(row.onToggleKey)}
                                                style={{ padding: 2 }}
                                            >
                                                {row.expanded
                                                    ? <KeyboardArrowDownIcon fontSize={'small'} />
                                                    : <KeyboardArrowRightIcon fontSize={'small'} />}
                                            </IconButton>
                                        ) : (
                                            <Box style={{ width: 28 }} />
                                        )}
                                        <Typography variant={'body2'} style={{ fontWeight: row.bold ? 600 : 400 }}>
                                            {row.label}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell align={'right'}>{dash(row.hours, row.hours != null)}</TableCell>
                                <TableCell align={'right'}>{row.used_hours}</TableCell>
                                <TableCell align={'right'}>{dash(row.days, row.days != null)}</TableCell>
                                <TableCell align={'right'}>{row.used_days}</TableCell>
                                <TableCell align={'right'}>{dash(row.citiesCount, row.citiesCount != null)}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            )}
        </Box>
    );
};
